import WeatherIcon from "@/components/WeatherIcon";

// Sample preview (shown before user requests a live preview).
const SAMPLE = [
  { day_name: "Today",     date_display: "Apr 22", icon: "cloud-sun",      high_f: 72, high_time: "3:00 PM", low_f: 54, low_time: "5:00 AM", condition: "Partly Cloudy",  precipitation_chance: 10, summary: "A mild day with thin clouds drifting through. Stays dry — no rain expected." },
  { day_name: "Tuesday",   date_display: "Apr 23", icon: "cloud-rain",     high_f: 66, high_time: "2:00 PM", low_f: 52, low_time: "6:00 AM", condition: "Light Rain",      precipitation_chance: 70, summary: "Light rain likely between 3 and 5 PM. Cooler and breezy by evening." },
  { day_name: "Wednesday", date_display: "Apr 24", icon: "cloud",          high_f: 64, high_time: "4:00 PM", low_f: 50, low_time: "5:00 AM", condition: "Overcast",        precipitation_chance: 20, summary: "Gray skies all day, but precipitation stays south." },
  { day_name: "Thursday",  date_display: "Apr 25", icon: "sun",            high_f: 71, high_time: "3:00 PM", low_f: 49, low_time: "6:00 AM", condition: "Clear",           precipitation_chance: 0,  summary: "Full sun returns — a great window for anything outdoors." },
  { day_name: "Friday",    date_display: "Apr 26", icon: "cloud-sun",      high_f: 74, high_time: "4:00 PM", low_f: 53, low_time: "6:00 AM", condition: "Mostly Clear",    precipitation_chance: 5,  summary: "Another dry, warm afternoon. Winds stay light out of the west." },
  { day_name: "Saturday",  date_display: "Apr 27", icon: "cloud-lightning",high_f: 77, high_time: "2:00 PM", low_f: 61, low_time: "5:00 AM", condition: "Thunderstorm",    precipitation_chance: 80, summary: "Afternoon thunderstorms likely 2-6 PM. Humid leading up, cooler after." },
  { day_name: "Sunday",    date_display: "Apr 28", icon: "cloud-rain",     high_f: 68, high_time: "1:00 PM", low_f: 55, low_time: "7:00 AM", condition: "Showers",         precipitation_chance: 60, summary: "Showers on and off through the morning, tapering by early afternoon." },
];

function ForecastRows({ rows, testPrefix = "" }) {
  return (
    <div className="divide-y divide-neutral-200">
      {rows.map((d, i) => {
        const wet = (d.precipitation_chance ?? 0) >= 60;
        const key = d.date ? `${d.date}-${d.day_name}` : `${d.day_name}-${i}`;
        return (
          <div
            key={key}
            data-testid={`${testPrefix}preview-row-${i}`}
            className={`relative px-5 py-4 hover:bg-[#FAFAFA] transition-colors ${wet ? "border-l-4 border-l-[#FFD600] pl-4" : "border-l-4 border-l-transparent"}`}
          >
            {/* Row 1 — Day + Date (own line for clarity) */}
            <div className="ticker-label !text-[11px] !font-bold mb-3" data-testid={`${testPrefix}preview-date-${i}`}>
              {d.day_name}{d.date_display ? ` · ${d.date_display}` : ""}
            </div>
            {/* Row 2 — Icon | High stacked | Low stacked | Summary */}
            <div className="grid grid-cols-[44px_68px_68px_1fr] gap-3 items-start">
              <div className="pt-1">
                <WeatherIcon name={d.icon} size={32} strokeWidth={1.75} />
              </div>
              <div className="text-center">
                <div className="ticker-label !text-[10px] !font-semibold">High</div>
                <div className="font-display text-2xl font-black leading-none mt-1">{d.high_f}°</div>
                <div className="text-[11px] text-neutral-500 mt-1">at {d.high_time}</div>
              </div>
              <div className="text-center">
                <div className="ticker-label !text-[10px] !font-semibold">Low</div>
                <div className="font-display text-2xl font-black leading-none mt-1">{d.low_f}°</div>
                <div className="text-[11px] text-neutral-500 mt-1">at {d.low_time}</div>
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider">
                  {d.condition} · {d.precipitation_chance}% precip
                </div>
                <div className="text-[13px] text-neutral-800 mt-1 leading-snug">{d.summary}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LocationBanner({ label, index, total }) {
  return (
    <div
      data-testid={`preview-location-banner-${index}`}
      className="flex items-center gap-3 px-5 py-3 bg-[#FFFBE6] border-b border-t border-black"
    >
      <span className="inline-flex min-w-[22px] h-[22px] items-center justify-center bg-black text-[#FFD600] font-display text-xs font-black px-1.5">
        {index + 1}
      </span>
      <div className="flex-1 min-w-0">
        <div className="ticker-label leading-tight">Location {index + 1} of {total}</div>
        <div className="text-[13px] font-semibold truncate" title={label}>{label}</div>
      </div>
    </div>
  );
}

export default function EmailPreview({ previews, data, heading = "Tomorrow at 7:00 AM" }) {
  // Backwards-compat: accept old `data` prop as a single-location forecast array.
  const blocks = previews && previews.length
    ? previews
    : data && data.length
    ? [{ label: null, forecast: data }]
    : null;

  return (
    <div className="border border-black bg-white" data-testid="email-preview">
      <div className="flex items-center justify-between px-5 py-3 border-b border-black bg-[#0A0A0A] text-white">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#FFD600]" />
          <span className="ticker-label !text-white/80">Your daily email — preview</span>
        </div>
        <div className="ticker-label !text-white/60">{heading}</div>
      </div>
      <div className="px-5 py-4 border-b border-neutral-200">
        <div className="font-display text-2xl font-black tracking-tight">Good morning.</div>
        <div className="text-[12px] text-neutral-600 mt-1">
          Synthesized from ECMWF · GFS · ICON · NAM · Open-Meteo · MET Norway
          {blocks && blocks.length > 1 && (
            <> · <span className="font-semibold text-neutral-800">{blocks.length} locations</span></>
          )}
        </div>
      </div>

      {!blocks ? (
        <ForecastRows rows={SAMPLE} />
      ) : blocks.length === 1 && !blocks[0].label ? (
        <ForecastRows rows={blocks[0].forecast} />
      ) : (
        blocks.map((b, idx) => (
          <div key={`${b.label || "loc"}-${idx}`} data-testid={`preview-location-${idx}`}>
            <LocationBanner label={b.label || `Location ${idx + 1}`} index={idx} total={blocks.length} />
            <ForecastRows rows={b.forecast} testPrefix={`loc${idx}-`} />
          </div>
        ))
      )}
    </div>
  );
}
